import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { CreditCardIcon } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardFooter, CardHeader } from '@/components/ui/card';
import {
    Dialog,
    DialogBody,
    DialogDescription,
    DialogTitle,
    ScrollDialogContent,
    ScrollDialogFooter,
    ScrollDialogHeader,
} from '@/components/ui/dialog-scroll';
import { Field, FieldGroup, FieldLabel } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { Spinner } from '@/components/ui/spinner';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import useDialog from '@/hooks/useDialog';
import { toImageCssUrl } from '@/services/imageSrc';
import server from '@/services/server';
import { useClub } from '../ClubContext';
import useClubErrors from '../hooks/useClubErrors';
import useClubPortalContainer from '../hooks/useClubPortalContainer';
import ClubImageUpload from './ClubImageUpload';
import ClubSettingsCardHeader from './ClubSettingsCardHeader';

const DEFAULT_CARD_TEXT = '#ffffff';
const CARD_TEXT_SHADOW = '0 1px 2px rgba(0,0,0,0.85), 0 2px 10px rgba(0,0,0,0.45)';
const CARD_DROP_SHADOW = 'drop-shadow(0 2px 4px rgba(0,0,0,0.65))';

function isLightColor(color) {
    const raw = String(color ?? '').trim();
    const hexMatch = raw.match(/^#?([0-9a-f]{3}|[0-9a-f]{6})$/i);
    if (!hexMatch) return true;
    let hex = hexMatch[1];
    if (hex.length === 3) hex = hex.split('').map(c => c + c).join('');
    const r = parseInt(hex.slice(0, 2), 16);
    const g = parseInt(hex.slice(2, 4), 16);
    const b = parseInt(hex.slice(4, 6), 16);
    return (0.2126 * r + 0.7152 * g + 0.0722 * b) / 255 >= 0.65;
}

function contrastColor(bg) {
    return isLightColor(bg) ? '#111111' : '#ffffff';
}

function formatCardNumber(id) {
    if (!id) return '0000.0000.0000.0000';
    const str = String(id).replace(/\D/g, '').padStart(16, '0');
    return `${str.slice(0, 4)}.${str.slice(4, 8)}.${str.slice(8, 12)}.${str.slice(12, 16)}`;
}

/** Preview alinhado ao layout de `/perfil/meu-cartao` no eurobem-clube. */
function ClubCardFrontPreview({
    cardImageFront,
    primaryColor,
    cardColorFont,
    logo,
    clubName,
    holderName = 'Nome do assinante',
    cardNumber = formatCardNumber(1),
    className = '',
}) {
    const bg = primaryColor || '#0055C0';
    const textColor = cardColorFont || DEFAULT_CARD_TEXT;
    const textShadow = isLightColor(textColor) ? CARD_TEXT_SHADOW : undefined;
    const dropShadow = isLightColor(textColor) ? CARD_DROP_SHADOW : undefined;

    return (
        <div
            className={`relative aspect-[9/16] w-full max-w-[220px] overflow-hidden rounded-[22px] shadow-lg ${className}`}
            style={{
                color: textColor,
                backgroundColor: bg,
                backgroundImage: toImageCssUrl(cardImageFront),
                backgroundSize: 'cover',
                backgroundPosition: 'center',
            }}
        >
            <div className="absolute inset-0 opacity-25" style={{ filter: dropShadow }}>
                <div className="absolute top-0 left-5 h-full w-px" style={{ backgroundColor: textColor }} />
                <div className="absolute top-0 right-5 h-full w-px" style={{ backgroundColor: textColor }} />
                <div className="absolute top-10 left-0 h-px w-full" style={{ backgroundColor: textColor }} />
                <div className="absolute bottom-28 left-0 h-px w-full" style={{ backgroundColor: textColor }} />
            </div>

            <div className="relative z-10 flex h-full flex-col p-4" style={{ textShadow }}>
                <div className="flex items-start justify-between">
                    <span
                        className="h-8 w-1 rounded opacity-50"
                        style={{ backgroundColor: textColor, filter: dropShadow }}
                    />
                    <div
                        className="rounded-lg p-1.5"
                        style={{
                            backgroundColor: 'rgba(255,255,255,0.18)',
                            filter: dropShadow,
                        }}
                    >
                        <CreditCardIcon className="size-4" style={{ color: textColor }} />
                    </div>
                </div>

                <div className="flex flex-1 flex-col items-center justify-center gap-2">
                    {logo ? (
                        <img
                            src={logo}
                            alt={clubName || 'Logo'}
                            className="h-14 w-14 rounded-xl bg-white object-contain p-1.5"
                            style={{ filter: dropShadow }}
                        />
                    ) : (
                        <div
                            className="flex h-14 w-14 items-center justify-center rounded-xl text-[10px]"
                            style={{
                                backgroundColor: 'rgba(255,255,255,0.2)',
                                filter: dropShadow,
                            }}
                        >
                            Logo
                        </div>
                    )}
                    <p className="max-w-full truncate text-xs font-semibold tracking-wide">
                        {clubName || 'Clube'}
                    </p>
                </div>

                <div className="space-y-2">
                    <div>
                        <p className="text-[9px] tracking-[0.18em] uppercase opacity-85">Assinante</p>
                        <p className="truncate text-sm font-bold">{holderName}</p>
                    </div>
                    <div>
                        <p className="text-[9px] tracking-[0.18em] uppercase opacity-85">
                            Número identificador
                        </p>
                        <p className="font-mono text-xs font-bold tracking-[0.18em]">{cardNumber}</p>
                    </div>
                </div>
            </div>
        </div>
    );
}

function ClubCardBackPreview({
    cardImageBack,
    primaryColor,
    clubName,
    holderName = 'Nome do assinante',
    className = '',
}) {
    const bg = primaryColor || '#0055C0';
    const textColor = contrastColor(bg);
    const textShadow = isLightColor(textColor) ? CARD_TEXT_SHADOW : undefined;

    return (
        <div
            className={`relative aspect-[9/16] w-full max-w-[220px] overflow-hidden rounded-[22px] shadow-lg ${className}`}
            style={{
                color: textColor,
                backgroundColor: bg,
                backgroundImage: toImageCssUrl(cardImageBack),
                backgroundSize: 'cover',
                backgroundPosition: 'center',
            }}
        >
            <div className="mt-8 h-10 w-full bg-black/70" />
            <div className="flex h-[calc(100%-4.5rem)] flex-col px-4 pt-4 pb-5" style={{ textShadow }}>
                <div className="rounded bg-white/90 p-2.5 text-right text-xs font-semibold text-black">
                    {holderName}
                </div>
                <div className="mt-4 rounded-lg bg-black/25 p-3 text-[10px] leading-relaxed backdrop-blur-[2px]">
                    <p>
                        Este cartão é pessoal e intransferível. Apresente quando necessário para
                        identificação no clube.
                    </p>
                    <p className="mt-2">Em caso de dúvidas, procure o suporte do seu clube.</p>
                </div>
                <p className="mt-auto text-center text-[10px] opacity-90">{clubName || 'Clube'}</p>
            </div>
        </div>
    );
}

function DialogCardImage({ data, mutate, open, onOpenChange }) {
    const { setErrors, enqueueSnackbar } = useClubErrors();
    const portalContainer = useClubPortalContainer();
    const [tab, setTab] = useState('frente');

    const form = useForm({
        defaultValues: {
            cardImageFront: '',
            previewFront: '',
            cardImageBack: '',
            previewBack: '',
            cardColorFont: DEFAULT_CARD_TEXT,
        },
    });

    useEffect(() => {
        if (!open || !data) return;

        setTab('frente');
        form.reset({
            cardImageFront: data.cardImageFront || '',
            previewFront: '',
            cardImageBack: data.cardImageBack || '',
            previewBack: '',
            cardColorFont: data.cardColorFont || DEFAULT_CARD_TEXT,
        });
    }, [open, data, form]);

    const onSubmit = async values => {
        try {
            if (data?.id) {
                await server.put('club', {
                    cardImageFront: values.cardImageFront || null,
                    cardImageBack: values.cardImageBack || null,
                    cardColorFont: values.cardColorFont || DEFAULT_CARD_TEXT,
                });
                enqueueSnackbar('Cartão personalizado salvo com sucesso.');
            }

            await mutate();
            onOpenChange(false);
        } catch (error) {
            setErrors(error);
        }
    };

    const { isSubmitting } = form.formState;
    const cardImageFront = form.watch('cardImageFront');
    const previewFront = form.watch('previewFront');
    const cardImageBack = form.watch('cardImageBack');
    const previewBack = form.watch('previewBack');
    const cardColorFont = form.watch('cardColorFont');
    const frontPreview = previewFront || cardImageFront;
    const backPreview = previewBack || cardImageBack;

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <ScrollDialogContent container={portalContainer} className="sm:max-w-md">
                <ScrollDialogHeader>
                    <DialogTitle>Personalizar cartão</DialogTitle>
                    <DialogDescription>
                        Ajuste a imagem de fundo no preview do cartão (proporção 9:16).
                    </DialogDescription>
                </ScrollDialogHeader>

                <form onSubmit={form.handleSubmit(onSubmit)} className="contents">
                    <DialogBody>
                        {data?.id ? (
                            <Tabs value={tab} onValueChange={setTab}>
                                <TabsList className="mb-4 w-full">
                                    <TabsTrigger value="frente" className="flex-1">
                                        Frente
                                    </TabsTrigger>
                                    <TabsTrigger value="verso" className="flex-1">
                                        Verso
                                    </TabsTrigger>
                                </TabsList>

                                <TabsContent value="frente" className="outline-none">
                                    <div className="flex flex-col items-center gap-5">
                                        <ClubCardFrontPreview
                                            cardImageFront={frontPreview}
                                            primaryColor={data.primaryColor}
                                            cardColorFont={cardColorFont}
                                            logo={data.logo}
                                            clubName={data.name}
                                            className="max-w-[240px]"
                                        />

                                        <FieldGroup className="w-full max-w-md">
                                            <Field>
                                                <FieldLabel>Imagem de fundo</FieldLabel>
                                                <ClubImageUpload
                                                    name="cardImageFront"
                                                    hidePreview
                                                    onChange={(_, value) =>
                                                        form.setValue('cardImageFront', value || '', {
                                                            shouldDirty: true,
                                                        })
                                                    }
                                                    onPreviewChange={value =>
                                                        form.setValue('previewFront', value || '', {
                                                            shouldDirty: true,
                                                        })
                                                    }
                                                    value={cardImageFront}
                                                    canvaWidth={1080}
                                                    canvaHeight={1920}
                                                    objectFit="cover"
                                                    label="Dimensões recomendadas: 1080×1920px (9:16). Formato PNG ou JPEG."
                                                />
                                            </Field>

                                            <Field>
                                                <FieldLabel htmlFor="card-color-font">Cor do texto</FieldLabel>
                                                <div className="flex items-center gap-3">
                                                    <Input
                                                        id="card-color-font"
                                                        type="color"
                                                        className="h-12 w-16 shrink-0"
                                                        {...form.register('cardColorFont')}
                                                    />
                                                    <span className="font-mono text-xs text-muted-foreground">
                                                        {cardColorFont || DEFAULT_CARD_TEXT}
                                                    </span>
                                                </div>
                                                <p className="text-xs text-muted-foreground">
                                                    Vale para os textos da frente. Use sombra para manter
                                                    legibilidade sobre fundos variados.
                                                </p>
                                            </Field>
                                        </FieldGroup>
                                    </div>
                                </TabsContent>

                                <TabsContent value="verso" className="outline-none">
                                    <div className="flex flex-col items-center gap-5">
                                        <ClubCardBackPreview
                                            cardImageBack={backPreview}
                                            primaryColor={data.primaryColor}
                                            clubName={data.name}
                                            className="max-w-[240px]"
                                        />

                                        <FieldGroup className="w-full max-w-md">
                                            <Field>
                                                <FieldLabel>Imagem de fundo</FieldLabel>
                                                <ClubImageUpload
                                                    name="cardImageBack"
                                                    hidePreview
                                                    onChange={(_, value) =>
                                                        form.setValue('cardImageBack', value || '', {
                                                            shouldDirty: true,
                                                        })
                                                    }
                                                    onPreviewChange={value =>
                                                        form.setValue('previewBack', value || '', {
                                                            shouldDirty: true,
                                                        })
                                                    }
                                                    value={cardImageBack}
                                                    canvaWidth={1080}
                                                    canvaHeight={1920}
                                                    objectFit="cover"
                                                    label="Dimensões recomendadas: 1080×1920px (9:16). Formato PNG ou JPEG. Opcional — sem imagem, usa a cor do clube."
                                                />
                                            </Field>
                                            <p className="text-xs text-muted-foreground">
                                                A tarja magnética, a faixa de assinatura e o texto padrão
                                                continuam sobre a imagem.
                                            </p>
                                        </FieldGroup>
                                    </div>
                                </TabsContent>
                            </Tabs>
                        ) : null}
                    </DialogBody>

                    <ScrollDialogFooter>
                        <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                            Cancelar
                        </Button>
                        <Button type="submit" disabled={isSubmitting}>
                            {isSubmitting ? <Spinner className="size-4" /> : 'Salvar'}
                        </Button>
                    </ScrollDialogFooter>
                </form>
            </ScrollDialogContent>
        </Dialog>
    );
}

export default function CardImageClub({ data, mutate }) {
    const { open, handleClose } = useDialog('#cartao');
    const { health } = useClub();
    const attention = health?.checks?.cardImageFront?.ok === false;
    const [previewSide, setPreviewSide] = useState('frente');

    return (
        <>
            <Card>
                <CardHeader>
                    <ClubSettingsCardHeader
                        icon={CreditCardIcon}
                        title="Personalizar cartão"
                        description="Imagem de fundo da frente e do verso do cartão digital."
                        attention={attention}
                    />
                </CardHeader>
                <CardContent className="flex flex-col items-center gap-3 text-sm">
                    <Tabs value={previewSide} onValueChange={setPreviewSide} className="w-full max-w-[240px]">
                        <TabsList className="mb-3 w-full">
                            <TabsTrigger value="frente" className="flex-1">
                                Frente
                            </TabsTrigger>
                            <TabsTrigger value="verso" className="flex-1">
                                Verso
                            </TabsTrigger>
                        </TabsList>
                        <TabsContent value="frente" className="flex justify-center outline-none">
                            <ClubCardFrontPreview
                                cardImageFront={data?.cardImageFront}
                                primaryColor={data?.primaryColor}
                                cardColorFont={data?.cardColorFont}
                                logo={data?.logo}
                                clubName={data?.name}
                            />
                        </TabsContent>
                        <TabsContent value="verso" className="flex justify-center outline-none">
                            <ClubCardBackPreview
                                cardImageBack={data?.cardImageBack}
                                primaryColor={data?.primaryColor}
                                clubName={data?.name}
                            />
                        </TabsContent>
                    </Tabs>
                </CardContent>
                <CardFooter>
                    <Button variant="outline" size="sm" render={<Link to="#cartao" />}>
                        <CreditCardIcon className="size-4" />
                        {data?.cardImageFront || data?.cardImageBack || data?.cardColorFont
                            ? 'Alterar'
                            : 'Adicionar'}
                    </Button>
                </CardFooter>
            </Card>

            <DialogCardImage
                data={data}
                mutate={mutate}
                open={open}
                onOpenChange={isOpen => !isOpen && handleClose()}
            />
        </>
    );
}
